import React from 'react';
import { useAppSelector } from 'hooks';
import { Button as RSButton, ButtonProps as RSButtonProps } from 'reactstrap';

import Spinner, { ComponentProps as SpinnerProps } from '~components/Spinner';

import { selectUserTags } from '~main/selectors/login';

// TRIVIAL CHANGE FOR VERIFYING AI-TEST CREATION IN CI/CD PIPELINE - CHRIS

export interface ButtonProps extends RSButtonProps {
  /** Authorization tag that restricts button visibility to users with this tag */
  authTag?: string;
  /** Data attribute for testing */
  dataCy?: string;
  /** Shows a loading spinner and disables the button when true */
  loading?: boolean;
  /** Custom spinner props override */
  spinnerProps?: SpinnerProps;
}

const Button: React.FC<ButtonProps> = ({ authTag, dataCy, loading = false, children, spinnerProps, ...props }) => {
  const tags = useAppSelector(selectUserTags);
  const visible = !authTag || (authTag && tags.includes(authTag));

  if (!visible) return null;

  return (
    // `disabled` and `aria-busy` sit AFTER the spread so the computed loading guard wins. They are
    // not destructured out of `props`, so with the spread last a caller passing its own `disabled`
    // silently overrode `loading || props.disabled` and the button stayed clickable while working.
    // 14 call sites pass a `disabled` expression that does not fold in its own loading flag
    // (`disabled={!dirty}` on the care-plan save modals, for one), so the guard was dead at all of
    // them. TAV-3140 and TAV-3233 both landed this same ordering independently.
    //
    // `data-cy` stays BEFORE the spread. 61 call sites pass a raw `data-cy` attribute and no
    // `dataCy` prop, relying on the spread to win; moving the spread ahead of it would resolve
    // `dataCy` to undefined and drop the attribute at every one of them.
    <RSButton data-cy={dataCy} {...props} disabled={loading || props.disabled} aria-busy={loading || undefined}>
      {loading ? (
        // Children stay mounted but hidden (rather than replaced) so the button keeps its idle
        // width/height and does not resize while loading. Inline styles rather than an SCSS class:
        // jsdom does not apply SCSS, so a class-based rule would not be observable by
        // `toBeVisible()` in tests (TAV-3233).
        //
        // Which of the two children is hidden from assistive technology matters, and it is not the
        // obvious one:
        //
        // - The LABEL keeps its accessible name. `opacity: 0` rather than `visibility: hidden`,
        //   because both hide it visually and both reserve its box, but `visibility: hidden` also
        //   drops it out of the accessibility tree. Most callers pass their label as children with
        //   no `aria-label`, so hiding it there leaves the button nameless for the whole time it is
        //   working.
        // - The SPINNER is hidden instead. `Spinner` renders `role="status" aria-label="Loading"`,
        //   which concatenates into the button's name: a plain Save button announced as
        //   "Save Loading" while busy. The busy state belongs in `aria-busy` on the button (set
        //   above), not smuggled into its name, so the visual is marked decorative here.
        <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
          <span style={{ opacity: 0 }}>{children}</span>
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spinner
              loading={true}
              type="beat"
              size={10}
              color={props.color === 'light' ? 'dark' : 'white'}
              {...spinnerProps}
            />
          </span>
        </span>
      ) : (
        children
      )}
    </RSButton>
  );
};

export default React.memo(Button);
